/**
 * Demo Control Panel - Floating overlay for demo mode
 * 
 * Appears on all pages when demo mode is active
 * Allows navigation between demo steps from any page
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import Icon from '../Icons';
import './styles.css';

// Default demo flows (fallback)
const DEFAULT_DEMO_FLOWS = [
  {
    id: 'continuity-architecture',
    title: 'Continuity Architecture Overview',
    route: '/continuity-architecture',
    requiresAuth: false,
    needsMemberId: false
  },
  {
    id: 'asset-segmentation',
    title: 'Segmentation of Assets',
    route: '/privacy-folders',
    requiresAuth: true,
    needsMemberId: true
  },
  {
    id: 'continuity-scenarios',
    title: 'Continuity Scenarios: How It Works',
    route: '/privacy-folders',
    requiresAuth: true,
    needsMemberId: true
  },
  {
    id: 'access-layers',
    title: 'Access Layers & Roles',
    route: '/emergency-access',
    requiresAuth: true,
    needsMemberId: false
  },
  {
    id: 'time-locked-release',
    title: 'Time-Locked Release Flow',
    route: '/family-vault',
    requiresAuth: true,
    needsMemberId: true
  },
  {
    id: 'emergency-access',
    title: 'Emergency Access Model',
    route: '/emergency-access',
    requiresAuth: true,
    needsMemberId: false
  },
  {
    id: 'audit-releases',
    title: 'Audit & Record of Releases',
    route: '/audit-trail',
    requiresAuth: true,
    needsMemberId: false
  },
  {
    id: 'continuity-timeline',
    title: 'Continuity Timeline View',
    route: '/continuity-timeline',
    requiresAuth: true,
    needsMemberId: true
  }
];

const DemoControlPanel = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth0();
  const [demoMode, setDemoMode] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [demoFlows, setDemoFlows] = useState(DEFAULT_DEMO_FLOWS);

  useEffect(() => {
    // Check if demo mode is active
    const checkDemoMode = () => {
      const demoState = sessionStorage.getItem('demoMode');
      if (demoState) {
        try {
          const state = JSON.parse(demoState);
          setDemoMode(state.active);
          setCurrentStep(state.currentStep || 0);

          // Load flows from sessionStorage if available
          if (state.flows && Array.isArray(state.flows)) {
            // Title mapping for all possible demo flows
            const titleMap = {
              'continuity-architecture': 'Continuity Architecture Overview',
              'access-layers': 'Access Layers & Roles Panel',
              'time-locked-release': 'Time-Locked Release Flow',
              'emergency-access': 'Emergency Access Model',
              'asset-segmentation': 'Segmentation of Assets',
              'continuity-scenarios': 'Continuity Scenarios: How It Works',
              'audit-releases': 'Audit & Record of Releases',
              'continuity-timeline': 'Continuity Timeline View',
              'fo-dashboard': 'Family Office Dashboard',
              'governance-walkthrough': 'Governance Walkthrough',
              'risk-graph': 'Risk Graph & Analytics',
              'cross-family-governance': 'Cross-Family Governance'
            };

            const flowsWithTitles = state.flows.map(flow => {
              const defaultFlow = DEFAULT_DEMO_FLOWS.find(f => f.id === flow.id);
              return {
                ...flow,
                title: titleMap[flow.id] || defaultFlow?.title || flow.route.split('/').pop().replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
              };
            });
            setDemoFlows(flowsWithTitles);
          } else {
            setDemoFlows(DEFAULT_DEMO_FLOWS);
          }
        } catch {
          setDemoMode(false);
          setDemoFlows(DEFAULT_DEMO_FLOWS);
        }
      } else {
        setDemoMode(false);
        setDemoFlows(DEFAULT_DEMO_FLOWS);
      }
    };

    checkDemoMode();

    // Listen for demo mode changes
    const interval = setInterval(checkDemoMode, 500);

    return () => clearInterval(interval);
  }, []);

  const navigateToDemo = useCallback((demo) => {
    if (demo.requiresAuth && !isAuthenticated) {
      alert('Please log in first to view this demo');
      return false;
    }

    if (demo.needsMemberId) {
      const currentMember = sessionStorage.getItem('currentMember');
      if (currentMember) {
        try {
          const member = JSON.parse(currentMember);
          if (demo.route === '/continuity-timeline') {
            navigate(`/continuity-timeline/${member._id}`);
          } else if (demo.route === '/family-vault') {
            navigate(`/family-vault/${member._id}`);
          } else {
            navigate(`${demo.route}/${member._id}`);
          }
        } catch {
          navigate('/member-selection');
          alert('Please select a member first');
          return false;
        }
      } else {
        navigate('/member-selection');
        alert('Please select a member first');
        return false;
      }
    } else {
      navigate(demo.route);
    }
    return true;
  }, [isAuthenticated, navigate]);

  const stopDemo = useCallback(() => {
    sessionStorage.removeItem('demoMode');
    setDemoMode(false);
    setCurrentStep(0);
    navigate('/demo-hub');
  }, [navigate]);

  const nextStep = useCallback(() => {
    if (currentStep < demoFlows.length - 1) {
      const nextStepIndex = currentStep + 1;
      const nextDemo = demoFlows[nextStepIndex];

      // Update demo state
      sessionStorage.setItem('demoMode', JSON.stringify({
        active: true,
        currentStep: nextStepIndex
      }));

      setCurrentStep(nextStepIndex);
      navigateToDemo(nextDemo);
    } else {
      // Demo complete
      stopDemo();
      alert('Demo complete! All screens shown.');
    }
  }, [currentStep, navigateToDemo, stopDemo]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      const prevStepIndex = currentStep - 1;
      const prevDemo = demoFlows[prevStepIndex];

      sessionStorage.setItem('demoMode', JSON.stringify({
        active: true,
        currentStep: prevStepIndex
      }));

      setCurrentStep(prevStepIndex);
      navigateToDemo(prevDemo);
    }
  }, [currentStep, navigateToDemo, demoFlows]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!demoMode) return;

    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (e.code === 'ArrowRight' || e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        nextStep();
      }
      if (e.code === 'ArrowLeft' || e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        prevStep();
      }
      if (e.code === 'Escape') {
        e.preventDefault();
        stopDemo();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [demoMode, nextStep, prevStep, stopDemo]);

  if (!demoMode) return null;

  const currentDemo = demoFlows[currentStep];
  const progress = ((currentStep + 1) / demoFlows.length) * 100;

  return (
    <div className={`demo-control-panel ${isMinimized ? 'minimized' : ''}`}>
      <div className="demo-panel-header">
        <div className="demo-panel-title">
          <Icon name="play" size={16} />
          <span>Demo Mode</span>
        </div>
        <div className="demo-panel-actions">
          <button
            className="demo-panel-btn"
            onClick={prevStep}
            disabled={currentStep === 0}
            title="Previous Step (← or P)"
          >
            <Icon name="chevron-left" size={16} />
          </button>
          <button
            className="demo-panel-btn"
            onClick={nextStep}
            disabled={currentStep === demoFlows.length - 1}
            title="Next Step (→ or N)"
          >
            <Icon name="chevron-right" size={16} />
          </button>
          <button
            className="demo-panel-btn"
            onClick={() => setIsMinimized(!isMinimized)}
            title="Minimize"
          >
            <Icon name={isMinimized ? "chevron-up" : "chevron-down"} size={16} />
          </button>
          <button
            className="demo-panel-btn stop"
            onClick={stopDemo}
            title="Stop Demo (Esc)"
          >
            <Icon name="x" size={16} />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          <div className="demo-panel-progress">
            <div className="demo-progress-bar">
              <div
                className="demo-progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="demo-progress-text">
              Step {currentStep + 1} of {demoFlows.length}: {currentDemo?.title}
            </div>
          </div>

          <div className="demo-panel-steps">
            {demoFlows.map((demo, index) => {
              // Calculate the original index from the flow ID to maintain consistent numbering
              const originalIndex = index;
              return (
                <div
                  key={`${demo.id}-${index}`}
                  className={`demo-step-item ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                  onClick={() => {
                    setCurrentStep(index);
                    sessionStorage.setItem('demoMode', JSON.stringify({
                      active: true,
                      currentStep: index,
                      flows: demoFlows.map(f => ({ id: f.id, route: f.route, requiresAuth: f.requiresAuth, needsMemberId: f.needsMemberId }))
                    }));
                    navigateToDemo(demo);
                  }}
                >
                  <div className="demo-step-number">{originalIndex + 1}</div>
                  <div className="demo-step-title">{demo.title}</div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default DemoControlPanel;

